import React from 'react';
import './ShiftCard.css';

const getAccentClass = (shift) => {
  // Open (unassigned) non-service shifts (e.g. PREP) always render green.
  if ((shift.event_type || shift.nonServiceType) && !shift.employee_id) return 'accent--non-service';
  if (shift.role === 'Stint') return 'accent--stint';
  if (shift.role === 'TBC') return 'accent--tbc';
  if (shift.shift_type) {
    if (Array.isArray(shift.shift_type)) {
      if (shift.shift_type.includes('Shift Manager') || shift.shift_type.includes('Manager')) return 'accent--manager';
      if (shift.shift_type.includes('Cleaner')) return 'accent--cleaner';
    } else if (typeof shift.shift_type === 'string') {
      if (shift.shift_type.toLowerCase().includes('manager')) return 'accent--manager';
      if (shift.shift_type.toLowerCase().includes('cleaner')) return 'accent--cleaner';
    }
  }
  return 'accent--staff';
};

const formatTime = (t) => {
  if (!t) return t;
  return t;
};

const MATCH_LABELS = {
  'match-individual': "Within person's ideal window",
  'match-role': "Within role's ideal window",
  'mismatch': 'Outside ideal window',
};

const BOUNDARY_LABELS = {
  'boundary-individual': "Start & end align with person's ideal shifts",
  'boundary-role': "Start & end align with role's ideal shifts",
  'boundary-mismatch': 'Start/end do not align with any ideal shift',
};

const TickSvg = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
    <path
      d="M2.5 6.5 L5 9 L9.5 3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CrossSvg = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
    <path
      d="M3 3 L9 9 M9 3 L3 9"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const MatchIndicator = ({ kind }) => {
  if (!MATCH_LABELS[kind]) return null;
  const label = MATCH_LABELS[kind];
  const isMismatch = kind === 'mismatch';
  const className = `shift-card__match shift-card__match--${kind}`;
  return (
    <span className={className} role="img" aria-label={label} title={label}>
      {isMismatch ? <CrossSvg /> : <TickSvg />}
    </span>
  );
};

const BoundaryIndicator = ({ kind }) => {
  if (!BOUNDARY_LABELS[kind]) return null;
  const label = BOUNDARY_LABELS[kind];
  const isMismatch = kind === 'boundary-mismatch';
  const className = `shift-card__boundary-match shift-card__boundary-match--${kind}`;
  return (
    <span className={className} role="img" aria-label={label} title={label}>
      {isMismatch ? <CrossSvg /> : <TickSvg />}
    </span>
  );
};

const ShiftCard = ({ shift, onShiftClick }) => {
  const accentClass = getAccentClass(shift);
  const isKeyHolderGap = shift.shift_type === 'key_holder_gap' || shift.shape_of_day_type === 'key_holder_gap';
  const timeText = `${formatTime(shift.startDisplay)} - ${formatTime(shift.endDisplay)}`;
  const label = shift.resolvedLabel || shift.shape_of_day_type || shift.role || '';
  const nameText = shift.role === 'Stint' ? 'Stint' : shift.role === 'TBC' ? (isKeyHolderGap ? 'Key holder' : 'TBC') : label;

  const setShiftOutcome = shift.setShiftOutcome;
  const isHonouredSetShift = setShiftOutcome && setShiftOutcome.status === 'HONOURED';
  const badgeTitle = isHonouredSetShift
    ? `Set shift (${setShiftOutcome.constraint_type})`
    : '';
  const idealMatch = shift.idealMatch;
  const boundaryMatch = shift.boundaryMatch;
  const matchTitle = MATCH_LABELS[idealMatch] || '';
  const boundaryTitle = BOUNDARY_LABELS[boundaryMatch] || '';
  const tooltip = `${shift.name || ''} - ${nameText} ${timeText} (${shift.length.toFixed(1)}h)` +
    (isKeyHolderGap ? ' — Key holder unavailable for this window' : '') +
    (isHonouredSetShift ? ` — ${badgeTitle}` : '') +
    (matchTitle ? ` — ${matchTitle}` : '') +
    (boundaryTitle ? ` — ${boundaryTitle}` : '');

  const hasTopRight = Boolean(MATCH_LABELS[idealMatch]);
  const hasBottomRight = Boolean(BOUNDARY_LABELS[boundaryMatch]);
  let pinClass = 'shift-card__pin';
  if (hasTopRight && hasBottomRight) pinClass += ' shift-card__pin--top-left';
  else if (hasTopRight) pinClass += ' shift-card__pin--with-match';
  else if (hasBottomRight) pinClass += ' shift-card__pin--top-right-clear';

  return (
    <div
      className={`shift-card${isKeyHolderGap ? ' shift-card--key-holder-gap' : ''}`}
      onClick={() => onShiftClick(shift)}
      title={tooltip}
    >
      <div className={`shift-card__accent ${accentClass}`} />
      <div className="shift-card__content">
        <span className="shift-card__time">{timeText}</span>
        <span className="shift-card__name">
          {isKeyHolderGap && <span aria-hidden="true" style={{ marginRight: 4 }}>🔑</span>}
          {nameText}
        </span>
      </div>
      <MatchIndicator kind={idealMatch} />
      <BoundaryIndicator kind={boundaryMatch} />
      {isHonouredSetShift && (
        <span
          className={pinClass}
          role="img"
          aria-label={badgeTitle}
          title={badgeTitle}
        >
          📌
        </span>
      )}
    </div>
  );
};

export default ShiftCard;
